/**
 * Apply favicon from General Settings (system-setting) to the document head.
 * Config favicon first; static favicon.ico only as fallback when none configured.
 */

import {
  getCachedBranding,
  loadPublicBranding,
  type BrandingSettings,
} from "@/utils/branding";

const LINK_ID = "app-dynamic-favicon";
const DEFAULT_FAVICON = `${process.env.BASE_URL || "/"}favicon.ico`;

function isSafeFaviconHref(href: string): boolean {
  const t = href.trim();
  if (!t) {
    return false;
  }
  const lower = t.toLowerCase();
  if (
    lower.startsWith("javascript:") ||
    lower.startsWith("data:") ||
    lower.startsWith("vbscript:")
  ) {
    return false;
  }
  return true;
}

function withCacheBust(href: string): string {
  try {
    const u = href.startsWith("http")
      ? new URL(href)
      : new URL(href, window.location.origin);
    u.searchParams.set("_fv", String(Date.now()));
    return u.toString();
  } catch {
    return href;
  }
}

function ensureFaviconLink(): HTMLLinkElement {
  let link = document.getElementById(LINK_ID) as HTMLLinkElement | null;
  if (!link) {
    link = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null;
    if (link) {
      link.id = LINK_ID;
    }
  }
  if (!link) {
    link = document.createElement("link");
    link.id = LINK_ID;
    link.rel = "icon";
    document.head.appendChild(link);
  }
  return link;
}

export function setDocumentFavicon(href: string | null | undefined): void {
  if (!href || typeof href !== "string") {
    return;
  }
  if (!isSafeFaviconHref(href)) {
    return;
  }

  const link = ensureFaviconLink();
  link.type = "";
  const lower = href.trim().toLowerCase();
  if (lower.endsWith(".svg")) {
    link.type = "image/svg+xml";
  } else if (lower.endsWith(".png")) {
    link.type = "image/png";
  } else if (lower.endsWith(".ico")) {
    link.type = "image/x-icon";
  }

  link.href = withCacheBust(href.trim());
}

/** Apply config favicon if present; otherwise optional default. */
export function applyBrandingFavicon(
  branding: BrandingSettings | null | undefined,
  useDefaultIfMissing = false
): void {
  const configured = branding?.favicon || null;
  if (configured) {
    setDocumentFavicon(configured);
    return;
  }
  if (useDefaultIfMissing) {
    setDocumentFavicon(DEFAULT_FAVICON);
  }
}

/** Apply cached config favicon immediately (no default flash). */
export function applyCachedFaviconIfAny(): void {
  applyBrandingFavicon(getCachedBranding(), false);
}

/**
 * Load public branding and apply favicon.
 * Config first; default only after load if none configured.
 */
export async function loadAndApplyPublicFavicon(): Promise<void> {
  applyCachedFaviconIfAny();
  const branding = await loadPublicBranding();
  applyBrandingFavicon(branding, true);
}

export function pickFaviconFromApiPayload(payload: unknown): string | null {
  if (!payload || typeof payload !== "object") {
    return null;
  }
  const p = payload as Record<string, unknown>;
  const direct = p.FullImagePathFavicon || p.favicon;
  if (typeof direct === "string" && direct) {
    return direct;
  }
  const nested = p.data;
  if (nested && typeof nested === "object") {
    const d =
      (nested as Record<string, unknown>).FullImagePathFavicon ||
      (nested as Record<string, unknown>).favicon;
    if (typeof d === "string" && d) {
      return d;
    }
  }
  return null;
}

/** Fetch singleton general settings and apply favicon (requires auth). */
export async function applyFaviconFromGeneralSettings(): Promise<void> {
  try {
    const { default: GeneralSettings } = await import(
      "@/API/GeneralSettings/GeneralSettings"
    );
    const api = new GeneralSettings();
    const rows = (await api.getAll({
      filter: `type|generalSettings|=`,
    })) as Array<{ FullImagePathFavicon?: string }>;
    const href = rows?.[0]?.FullImagePathFavicon;
    if (href) {
      setDocumentFavicon(href);
    } else {
      setDocumentFavicon(DEFAULT_FAVICON);
    }
  } catch {
    applyCachedFaviconIfAny();
  }
}
